import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Popper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Search, Clear } from "@material-ui/icons";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import AutorenewRoundedIcon from '@material-ui/icons/AutorenewRounded';
import ChatOutlinedIcon from '@material-ui/icons/ChatOutlined';
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { IAppState } from "../../../../store";
import { IVehicle, VehicleServiceStatus } from "../../../../reducers/bookings/types";
import { StyledTableCell, StyledTableRow } from "../../../StyledTable";
import { GET_VEHICLES_FOR_BUSINESS_CUSTOMER, GET_VEHICLES_FOR_INDIVIDUAL_CUSTOMER } from "../../../../graphql/vehicles/getVehicles";
import { getSignedUrl } from "../../../../utils/getSignedUrl";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import AssignDriverDialog from './AssignDriverDialog';
import { UserRole } from '../../../../reducers/auth/types';
import { getLocalizedBookedSyntex, getLocalizedBookingSyntex, getLocalizedDateFormat } from "../../../../utils/localized.syntex";
import { DATE_TYPE, getVehicleTypeIcon } from "../../../utils";
import { getLocalisedLicense } from "../../../../utils/localized.syntex";
import { capitalizeFirstLetter } from "../../utils";
import { useTranslation } from "react-i18next";
import styles from "./index.module.css";
import CloseIcon from "@material-ui/icons/Close";
import { DateTime } from "luxon";

const useStyles = makeStyles((theme) => ({
  chip: {
    fontSize: "12px",
    height: "24px",
    borderRadius: "12px",
    fontWeight: 500
  },
  filterCard: {
    padding: "16px",
    marginBottom: "16px",
    backgroundColor: "#f5f5f5"
  },
  tabs: {
    marginBottom: "16px",
    backgroundColor: "#fff",
    borderRadius: "4px"
  },
  thumbnail: {
    width: "60px",
    height: "60px",
    borderRadius: "4px",
    overflow: "hidden",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover"
    }
  },
  driverButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    borderRadius: "4px",
    backgroundColor: "#e3f2fd",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#bbdefb"
    }
  },
  noWhiteSpace: {
    width: '1%',
  },
}));

type LocationState = {
  showActiveVehicles?: boolean;
  showAllocatedVehicles?: boolean;
  showFailedMotVehicles?: boolean;
  showUpcomingMotVehicles?: boolean;
  showMileageLimitExceededVehicles?: boolean;
  showApproachingMileageLimitVehicles?: boolean;
  showOverdueServiceVehicles?: boolean;
  showUpcomingServiceVehicles?: boolean;
};

const Vehicles = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const snackbar = useSnackBar();
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>("");
  const [vehicleCheckFilter, setVehicleCheckFilter] = useState<"pending" | "completed" | null>(null);
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { country } = website.organisation.address;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [sortBy, setSortBy] = useState<string>("licencePlate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [vehicles, setVehicles] = useState<IVehicle[]>([]);
  const [allVehicles, setAllVehicles] = useState<IVehicle[]>([]);
  const [assignDriverDialog, setAssignDriverDialog] = useState<{ open: boolean, vehicle?: IVehicle }>({ open: false });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const [getVehicles, { loading: vehiclesLoading, data: vehiclesData, error }] =
    useLazyQuery(GET_VEHICLES_FOR_BUSINESS_CUSTOMER, {
      fetchPolicy: "cache-and-network",
    });

  const [
    getVehiclesForIndividualCustomer,
    { loading: vehiclesForIndividualCustomerLoading, data: vehiclesDataForIndividualCustomer },
  ] = useLazyQuery(GET_VEHICLES_FOR_INDIVIDUAL_CUSTOMER, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("vehicleCheck");
    if (filterParam === "pending" || filterParam === "completed") {
      setVehicleCheckFilter(filterParam);
    } else {
      setVehicleCheckFilter(null);
    }
  }, [location.search]);

  useEffect(() => {
    if (user?.businesscustomers) {
      getVehicles({
        variables: {
          businessCustomerId: user.businesscustomers.id,
          ...(user.role === UserRole.DRIVER && { driver: user.customerId })
        },
      })
    } else if (user?.customer && user?.customerId) {
      getVehiclesForIndividualCustomer({
        variables: {
          customerId: user.customerId,
        },
      })
    }
  }, [user]);

  useEffect(() => {
    const fetchAndSetVehicles = async () => {
      let vehicleData;
      if (user?.businesscustomers && vehiclesData && vehiclesData.getVehiclesForBusinessCustomer) {
        vehicleData = vehiclesData.getVehiclesForBusinessCustomer
      } else if (
        user?.customer &&
        vehiclesDataForIndividualCustomer &&
        vehiclesDataForIndividualCustomer.getVehiclesForIndividualCustomer
      ) {
        vehicleData = vehiclesDataForIndividualCustomer.getVehiclesForIndividualCustomer
      }

      if (vehicleData) {
        const reShapeVehicles = []
        for (const vehicle of vehicleData) {
          if (!vehicle.isGhostVehicle) {
            const file = await getVehicleImage(vehicle.imageUrl);
            if (file) {
              reShapeVehicles.push({ ...vehicle, imageUrl: file });
            } else {
              reShapeVehicles.push(vehicle);
            }
          }
        }
        setAllVehicles(reShapeVehicles);
      }
    };

    fetchAndSetVehicles();
  }, [vehiclesData, vehiclesDataForIndividualCustomer]);

  const isVehicleCheckCompleted = (vehicle: IVehicle) => {
    const vehicleStateTimestamp =
      vehicle?.vehicleState?.updatedAt || vehicle?.vehicleState?.createdAt;
    if (!vehicleStateTimestamp) {
      return false;
    }
    let lastCheck = DateTime.fromISO(vehicleStateTimestamp, { setZone: true });
    if (!lastCheck.isValid) {
      lastCheck = DateTime.fromISO(vehicleStateTimestamp);
    }
    if (!lastCheck.isValid) {
      return false;
    }
    const lastCheckStart = lastCheck.toUTC().startOf("day");
    const todayStart = DateTime.utc().startOf("day");
    const daysDifference = todayStart.diff(lastCheckStart, "days").days;
    if (!Number.isFinite(daysDifference)) {
      return false;
    }
    return daysDifference <= 1;
  };

  const matchesVehicleCheckFilter = (vehicle: IVehicle) => {
    if (!vehicleCheckFilter) {
      return true;
    }
    const completed = isVehicleCheckCompleted(vehicle);
    return vehicleCheckFilter === "completed" ? completed : !completed;
  };

  useEffect(() => {
    let filtered = [...allVehicles];

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((vehicle) =>
        vehicle.licencePlate.toLowerCase().includes(searchLower) ||
        vehicle.make?.toLowerCase().includes(searchLower) ||
        vehicle.model?.toLowerCase().includes(searchLower) ||
        vehicle.activeBookingSchedule?.booking?.referenceNumber?.toLowerCase().includes(searchLower)
      );
    }

    if (location.state?.showActiveVehicles) {
      filtered = filtered.filter((vehicle) => {
        if (vehicle.activeBookingSchedule?.booking?.dropoffDateTime) {
          const dropoff = DateTime.fromISO(
            vehicle.activeBookingSchedule?.booking?.dropoffDateTime
          );
          const now = DateTime.now().setZone(
            vehicle.activeBookingSchedule?.booking?.branch?.timeZone || "UTC"
          );
          return dropoff > now;
        }
      });
    }

    if (location.state?.showAllocatedVehicles) {
      filtered = filtered.filter((vehicle) => vehicle.activeBookingSchedule?.driver?.id);
    }

    if (location.state?.showFailedMotVehicles) {
      filtered = filtered.filter((vehicle) => {
        return vehicle.vehicleTestReports?.some((report: any) => report.testResult === "FAIL");
      });
    }

    if (location.state?.showUpcomingMotVehicles) {
      filtered = filtered.filter((vehicle) => {
        const reports = vehicle.vehicleTestReports ?? [];
        return reports.some((report: any) => {
          const { testDate } = report;
          const timeZone = vehicle.activeBookingSchedule?.booking?.branch?.timeZone || "UTC"
          let parsedDate = testDate
            ? DateTime.fromISO(testDate, { zone: timeZone })
            : null;
          if (parsedDate && !parsedDate.isValid) {
            parsedDate = DateTime.fromFormat(testDate, "d MMMM yyyy", { zone: timeZone });
          }
          if (!parsedDate?.isValid) return false;
          const today = DateTime.now().setZone(timeZone);
          const pickupDateISO = vehicle.activeBookingSchedule?.booking?.pickupDateTime;
          const dropoffDateISO = vehicle.activeBookingSchedule?.booking?.dropoffDateTime;
          const pickupDate = pickupDateISO ? DateTime.fromISO(pickupDateISO) : null;
          const dropoffDate = dropoffDateISO ? DateTime.fromISO(dropoffDateISO) : null;
          return (
            parsedDate > today &&
            pickupDate !== null &&
            dropoffDate !== null &&
            parsedDate >= pickupDate &&
            parsedDate <= dropoffDate
          );
        });
      });
    }

    if (location.state?.showMileageLimitExceededVehicles || location.state?.showApproachingMileageLimitVehicles) {
      filtered = filtered.filter((vehicle) => {
        const booking = vehicle.activeBookingSchedule?.booking;
        const vehicleGroup = booking?.vehicleGroups?.[0];
        if (!vehicleGroup || vehicleGroup.unlimitedMileage) return false;
        const mileageLimit = vehicleGroup.mileageLimit ?? 0;
        if (mileageLimit === 0) return false;
        const initialOdometer = vehicle.activeBookingSchedule?.dropoffCheck?.vehicleState?.odometerReading ?? 0;
        const currentOdometer = vehicle.vehicleState?.odometerReading ?? initialOdometer;
        const kmDriven = currentOdometer - initialOdometer;
        const showExceeded = location.state?.showMileageLimitExceededVehicles;
        const showApproaching = location.state?.showApproachingMileageLimitVehicles;
        if (showExceeded && kmDriven >= mileageLimit) return true;
        if (showApproaching && kmDriven >= 0.9 * mileageLimit && kmDriven < mileageLimit) return true;
        return false;
      });
    }

    if (location.state?.showOverdueServiceVehicles || location.state?.showUpcomingServiceVehicles) {
      filtered = filtered.filter((vehicle) => {
        const pickupDate = vehicle.activeBookingSchedule?.booking?.pickupDateTime
          ? DateTime.fromISO(vehicle.activeBookingSchedule.booking.pickupDateTime)
          : null;
        const dropoffDate = vehicle.activeBookingSchedule?.booking?.dropoffDateTime
          ? DateTime.fromISO(vehicle.activeBookingSchedule.booking.dropoffDateTime)
          : null;
        if (!pickupDate || !dropoffDate) return false;
        const today = DateTime.now().setZone(vehicle.activeBookingSchedule?.booking?.branch?.timeZone || "UTC");
        let hasOverdue = false;
        let hasUpcoming = false;
        for (const service of vehicle.services || []) {
          const startDate = DateTime.fromISO(service.startDate);
          const completionDate = service.completionDate ? DateTime.fromISO(service.completionDate) : null;
          const isWithinBookingRange =
            (startDate >= pickupDate && startDate <= dropoffDate) ||
            (completionDate && completionDate >= pickupDate && completionDate <= dropoffDate);
          if (!isWithinBookingRange) continue;
          // overdue logic
          if (
            completionDate &&
            completionDate < today &&
            service.status !== VehicleServiceStatus.COMPLETED
          ) {
            hasOverdue = true;
          }
          // upcoming logic
          if (startDate > today) {
            hasUpcoming = true;
          }
        }
        // Apply filters based on flags
        if (location.state?.showOverdueServiceVehicles && hasOverdue) return true;
        if (location.state?.showUpcomingServiceVehicles && hasUpcoming) return true;
        return false;
      });
    }
    if (vehicleCheckFilter) {
      filtered = filtered.filter((vehicle) => matchesVehicleCheckFilter(vehicle));
    }

    filtered.sort((a, b) => {
      const aValue = (a as any)[sortBy];
      const bValue = (b as any)[sortBy];
      if (!aValue) return 1;
      if (!bValue) return -1;

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortOrder === "asc" ? comparison : -comparison;
    });

    const startIndex = page * rowsPerPage;
    const paginatedVehicles = filtered.slice(startIndex, startIndex + rowsPerPage);

    setVehicles(paginatedVehicles);
  }, [allVehicles, searchTerm, sortBy, sortOrder, page, rowsPerPage, vehicleCheckFilter, location.state]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  }, []);

  const handleTabChange = useCallback((event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleRequestSort = useCallback((property: string) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  }, [sortBy, sortOrder]);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setPage(0);
  }, []);

  const getTotalCount = () => {
    let filtered = [...allVehicles];
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((vehicle) =>
        vehicle.licencePlate.toLowerCase().includes(searchLower) ||
        vehicle.make?.toLowerCase().includes(searchLower) ||
        vehicle.model?.toLowerCase().includes(searchLower) ||
        vehicle.activeBookingSchedule?.booking?.referenceNumber?.toLowerCase().includes(searchLower)
      );
    }
    if (vehicleCheckFilter) {
      filtered = filtered.filter((vehicle) => matchesVehicleCheckFilter(vehicle));
    }
    return filtered.length;
  };

  const totalCount = getTotalCount();

  const clearVehicleCheckFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete("vehicleCheck");
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
  };

  const normalizedFilter = filter.trim().toLowerCase();

  const filteredVehicles = vehicles.filter((vehicle) => {
    const matchesSearch = normalizedFilter
      ? vehicle.licencePlate?.toLowerCase().includes(normalizedFilter)
      : true;
    return matchesSearch && matchesVehicleCheckFilter(vehicle);
  });

  const getVehicleImage = async (url: string) => {
    if (url) {
      try {
        const file = await getSignedUrl(url);
        return file;
      } catch (error: any) {
        snackbar({
          message: error,
          variant: SnackBarVariant.ERROR,
        });
      }
    }
  };


  const loading = user?.businesscustomers ? vehiclesLoading : vehiclesForIndividualCustomerLoading;

  return (
    <>
      {assignDriverDialog.open && user?.businesscustomers?.approvedDrivers && (
        <AssignDriverDialog
          open={assignDriverDialog.open}
          onClose={() => {
            setAssignDriverDialog({
              ...assignDriverDialog,
              open: false
            });
          }}
          drivers={user?.businesscustomers?.approvedDrivers}
          vehicle={assignDriverDialog.vehicle}
          onSucces={(driver) => {
            const _vehicles = allVehicles.map((v => {
              if (v.activeBookingSchedule.id === assignDriverDialog.vehicle?.activeBookingSchedule.id) {
                return {
                  ...v,
                  activeBookingSchedule: {
                    ...v.activeBookingSchedule,
                    driver
                  }
                }
              }
              return v
            }));
            setAllVehicles(_vehicles);
          }}
        />
      )}

      <div style={{ padding: "24px" }}>
        <Box display="flex" alignItems="center" style={{ marginBottom: "24px" }}>
          <Typography variant="h4" style={{ fontWeight: 600, fontSize: "2rem" }}>
            {user?.role === UserRole.DRIVER ? t("root:assigned") : getLocalizedBookedSyntex(country)} {t("root:vehicles")}
          </Typography>
        </Box>


        {vehicleCheckFilter && (
          <div className={styles.activeFilter}>
            <Typography variant="body2" className={styles.activeFilterText}>
              {vehicleCheckFilter === "pending" ? t("root:vehicleChecksPending") : t("root:vehicleChecksCompleted")}
            </Typography>
            <Tooltip title={t("root:dismiss")}>
              <IconButton size="small" onClick={clearVehicleCheckFilter}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        )}

        <Card className={classes.filterCard}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                size="small"
                placeholder={`${t("root:search")} ${getLocalisedLicense(country)} ${t("root:plate")}, Make, Model, or Booking`}
                value={searchInput}
                onChange={handleSearchChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchInput("");
                          setSearchTerm("");
                        }}
                        edge="end"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button fullWidth variant="outlined" startIcon={<Clear />} onClick={handleClearFilters}>
                Clear All
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Box style={{ position: "relative" }}>
          {loading && (
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255,255,255,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <TableContainer style={{ maxHeight: "600px" }}>
            <Table stickyHeader aria-label="vehicles table">
              <TableHead>
                <TableRow>
                  <StyledTableCell></StyledTableCell>
                  <StyledTableCell>
                    <TableSortLabel
                      active={sortBy === "licencePlate"}
                      direction={sortBy === "licencePlate" ? sortOrder : "asc"}
                      onClick={() => handleRequestSort("licencePlate")}
                    >
                      {getLocalisedLicense(country)} {t("root:plate")}
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell>{getLocalizedBookingSyntex(country)}</StyledTableCell>
                  <StyledTableCell>
                    <TableSortLabel
                      active={sortBy === "make"}
                      direction={sortBy === "make" ? sortOrder : "asc"}
                      onClick={() => handleRequestSort("make")}
                    >
                      {t("root:make")} / {t("root:model")} / {t("root:color")}
                    </TableSortLabel>
                  </StyledTableCell>
                  {user?.role === UserRole.ADMIN_USER && <StyledTableCell>{t("root:driverAssigned")}</StyledTableCell>}
                  <StyledTableCell>
                    {user?.role === UserRole.DRIVER && t("root:note")}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.length > 0 ? vehicles.map((vehicle, index) => {
                  const driver = user?.businesscustomers?.approvedDrivers.find((driver => driver?.id === vehicle.activeBookingSchedule.driver?.id));
                  const encodedNote = encodeURIComponent(vehicle.activeBookingSchedule.driver?.note || "");
                  const searchParams = new URLSearchParams();
                  searchParams.set("booking", vehicle.activeBookingSchedule.booking.id || "");
                  searchParams.set("note", encodedNote);
                  return (
                    <>
                      <StyledTableRow
                        key={index}
                        onClick={() =>
                          history.push(`/account/vehicles/${vehicle.id}?${searchParams.toString()}`)
                        }
                      >
                        <StyledTableCell className={classes.noWhiteSpace}>
                          <div className={styles.thumbnail}>
                            {vehicle.imageUrl ? (
                              <img
                                className="img-cover"
                                src={vehicle.imageUrl}
                                alt={t("root:vehicleImage")}
                              />
                            ) : (
                              <img
                                className="img-cover"
                                src={getVehicleTypeIcon(capitalizeFirstLetter(vehicle.bodyType))}
                                alt={t("root:vehicleImage")}
                                style={{ objectFit: "contain" }}
                              />
                            )}
                          </div>
                        </StyledTableCell>
                        <StyledTableCell>
                          {vehicle.licencePlate}
                        </StyledTableCell>
                        <StyledTableCell>
                          {vehicle.activeBookingSchedule.booking.referenceNumber}<br />
                          <span className="opacity50">
                            {vehicle.activeBookingSchedule.booking.pickupDateTime &&
                              getLocalizedDateFormat(country, vehicle.activeBookingSchedule.booking.pickupDateTime, DATE_TYPE.EXPANDED, vehicle.activeBookingSchedule.booking.branch?.timeZone)
                            } to<br />
                            {vehicle.activeBookingSchedule.booking.dropoffDateTime &&
                              getLocalizedDateFormat(country, vehicle.activeBookingSchedule.booking.dropoffDateTime, DATE_TYPE.EXPANDED, vehicle.activeBookingSchedule.booking.branch?.timeZone)
                            }
                          </span>

                        </StyledTableCell>
                        <StyledTableCell>
                          {vehicle.make} / {vehicle.model} / {vehicle.colour}
                        </StyledTableCell>
                        {user?.role === UserRole.ADMIN_USER && <StyledTableCell>
                          {driver ?
                            <div className="flex">
                              <span
                                className={styles.driverButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAssignDriverDialog({
                                    open: true,
                                    vehicle
                                  })
                                }}
                              >
                                <p>{`${driver.firstName} ${driver.lastName}`}</p>
                                <AutorenewRoundedIcon style={{ fontSize: 16, marginLeft: 3 }} />
                              </span>
                            </div>
                            :
                            <IconButton
                              size="medium"
                              edge="start"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssignDriverDialog({
                                  open: true,
                                  vehicle
                                })
                              }}
                            >
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          }</StyledTableCell>}
                        <StyledTableCell onClick={(e) => e.stopPropagation()}>
                          {driver &&
                            <Tooltip
                              title="Note"
                              placement="top"
                              PopperComponent={(props) => <Popper {...props} transition>
                                <div className="" style={{ maxWidth: 300, background: "#fff", border: "1px solid #e2e2e2", borderRadius: 6, boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)" }}>
                                  <Typography variant="body2" style={{ padding: "8px 14px", borderBottom: "1px solid #e2e2e2", fontWeight: 700 }}>
                                    {vehicle.activeBookingSchedule.driver?.note ? "Note" : "No note added!"}
                                  </Typography>
                                  {vehicle.activeBookingSchedule.driver?.note && <Typography variant="body2" style={{ padding: "8px 14px 12px 14px" }}>
                                    {vehicle.activeBookingSchedule.driver?.note || ""}
                                  </Typography>}
                                </div>
                              </Popper>}
                            >
                              <span className={styles.driverButton}>
                                <ChatOutlinedIcon style={{ fontSize: 16 }} />
                              </span>
                            </Tooltip>}
                        </StyledTableCell>
                      </StyledTableRow>
                    </>
                  );
                }) : (
                  <TableRow>
                    <StyledTableCell colSpan={6} align="center">
                      <Typography style={{ opacity: 0.5, padding: "40px 0" }}>
                        {user?.role === UserRole.DRIVER ? t("root:vehicleAppearOnceAssigned") : t("root:noRecordFound")}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 100]}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    marginTop: "auto"
                  },
                },
              },
            }}
          />
        </Box>
      </div>
    </>
  );
};

export default Vehicles;
